import React, { useState, useEffect, useCallback, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { sdsRadius } from '../theme/solutionsDesignSystem';

const useStyles = makeStyles(() => ({
    zoomContainer: {
        position: 'relative',
        display: 'block',
        width: '100%',
        maxWidth: '100%',
        cursor: 'pointer',
        userSelect: 'none',
        '&:hover $innerWrapper': {
            transform: 'scale(1.02)',
        },
    },
    innerWrapper: {
        display: 'block',
        width: '100%',
        transition: 'transform 300ms cubic-bezier(0.16, 1, 0.3, 1)',
        willChange: 'transform',
        transformOrigin: 'center center',
    },
    modalOverlay: {
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        cursor: 'zoom-out',
        background: 'rgba(43, 44, 46, 0.88)',
        backdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        animation: '$fadeIn 180ms ease-out',
    },
    modalContent: {
        position: 'relative',
        maxWidth: 'min(94vw, 1200px)',
        maxHeight: 'min(90vh, 900px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        outline: 'none',
        cursor: 'default',
        width: '100%',
    },
    modalVideo: {
        maxWidth: '100%',
        maxHeight: 'min(84vh, 840px)',
        width: '100%',
        height: 'auto',
        aspectRatio: '16 / 10',
        objectFit: 'cover',
        borderRadius: sdsRadius.sm,
        boxShadow: '0 24px 48px rgba(0, 0, 0, 0.45)',
        background: '#000',
    },
    modalCaption: {
        marginTop: 12,
        color: 'rgba(255, 255, 255, 0.85)',
        fontSize: 14,
        textAlign: 'center',
        maxWidth: '80%',
    },
    closeBtn: {
        position: 'fixed',
        top: 24,
        right: 24,
        width: 40,
        height: 40,
        borderRadius: sdsRadius.sm,
        border: 0,
        background: '#FFFFFF',
        color: '#2B2C2E',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.25)',
        transition: 'transform 120ms ease, background 120ms ease',
        '&:hover': {
            background: '#F4EEEE',
            transform: 'scale(1.05)',
        },
    },
    '@keyframes fadeIn': {
        from: { opacity: 0 },
        to: { opacity: 1 },
    },
}));

const CloseIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
        <path d="M6 6l12 12M18 6L6 18" />
    </svg>
);

const PbdqVideoLightboxWrapper = ({
    src,
    caption,
    className,
    children,
}) => {
    const classes = useStyles();
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);
    const modalVideoRef = useRef(null);
    const savedTimeRef = useRef(0);

    const handleOpen = () => {
        // Find child video in DOM to capture current playback time
        if (containerRef.current) {
            const childVid = containerRef.current.querySelector('video');
            if (childVid && !isNaN(childVid.currentTime)) {
                savedTimeRef.current = childVid.currentTime;
            }
        }
        setIsOpen(true);
    };

    const handleClose = () => {
        // When modal closes, sync the time back to the inline preview video
        if (modalVideoRef.current && containerRef.current) {
            const childVid = containerRef.current.querySelector('video');
            if (childVid && !isNaN(modalVideoRef.current.currentTime)) {
                childVid.currentTime = modalVideoRef.current.currentTime;
            }
        }
        setIsOpen(false);
    };

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Escape') {
            handleClose();
        }
    }, []);

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            const originalOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';

            // Sync playback time to modal video
            if (modalVideoRef.current && savedTimeRef.current > 0) {
                modalVideoRef.current.currentTime = savedTimeRef.current;
            }

            return () => {
                document.removeEventListener('keydown', handleKeyDown);
                document.body.style.overflow = originalOverflow;
            };
        }
    }, [isOpen, handleKeyDown]);

    const videoSrc = src || (children && children.props && children.props.src);

    return (
        <>
            <div
                ref={containerRef}
                role="button"
                tabIndex={0}
                aria-label={caption || 'Play and expand video'}
                onClick={handleOpen}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleOpen();
                    }
                }}
                className={clsx(classes.zoomContainer, className)}
            >
                <div className={classes.innerWrapper}>
                    {children}
                </div>
            </div>

            {isOpen && (
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label={caption || 'Video preview'}
                    className={classes.modalOverlay}
                    onClick={handleClose}
                >
                    <div
                        className={classes.modalContent}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <video
                            ref={modalVideoRef}
                            src={videoSrc}
                            autoPlay
                            muted
                            loop
                            controls
                            playsInline
                            className={classes.modalVideo}
                            onLoadedMetadata={() => {
                                if (modalVideoRef.current && savedTimeRef.current > 0) {
                                    modalVideoRef.current.currentTime = savedTimeRef.current;
                                }
                            }}
                        />
                        {caption && (
                            <div className={classes.modalCaption}>{caption}</div>
                        )}
                    </div>
                    <button
                        type="button"
                        aria-label="Close video preview"
                        onClick={handleClose}
                        className={classes.closeBtn}
                    >
                        <CloseIcon />
                    </button>
                </div>
            )}
        </>
    );
};

export default PbdqVideoLightboxWrapper;
